/**
 * @fileoverview Mobile-Friendly Styles Utilities
 * 
 * @description
 * Utilities para aplicar estilos touch-friendly em componentes MUI.
 * Garante touch targets mínimos de 44x44px (iOS) / 48x48px (Android).
 * 
 * @version 1.0.0
 * @see RELATORIO_DIA2_5_MOBILE_BASIC.md
 */

import { SxProps, Theme } from '@mui/material';
import { touchTargets, mobileSpacing } from '../styles/design-tokens';

// ============================================================================
// BUTTON STYLES
// ============================================================================

/**
 * Touch-friendly styles for MUI Button component
 * Ensures 44px minimum height (iOS guideline)
 */
export const touchFriendlyButton: SxProps<Theme> = {
  minHeight: touchTargets.button.minHeight,
  paddingX: touchTargets.button.paddingX,
  paddingY: touchTargets.button.paddingY,
};

/**
 * Touch-friendly styles for MUI IconButton component
 * Ensures 44x44px minimum size
 */
export const touchFriendlyIconButton: SxProps<Theme> = {
  minWidth: touchTargets.iconButton.minWidth,
  minHeight: touchTargets.iconButton.minHeight,
  padding: touchTargets.iconButton.padding,
};

/**
 * Touch-friendly styles for MUI ToggleButton component
 * Ensures 44px minimum height + comfortable width for labels
 */
export const touchFriendlyToggleButton: SxProps<Theme> = {
  minHeight: touchTargets.toggleButton.minHeight,
  minWidth: touchTargets.toggleButton.minWidth,
  paddingX: touchTargets.toggleButton.paddingX,
  paddingY: touchTargets.toggleButton.paddingY,
};

/**
 * Touch-friendly styles for MUI Chip component
 * Chips can be smaller (32px) as they're not primary actions
 */
export const touchFriendlyChip: SxProps<Theme> = {
  minHeight: touchTargets.chip.minHeight,
  paddingX: touchTargets.chip.paddingX,
  paddingY: touchTargets.chip.paddingY,
};

/**
 * Touch-friendly styles for MUI TextField component
 * Ensures input has 44px minimum height for comfortable tapping
 */
export const touchFriendlyTextField: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    minHeight: touchTargets.button.minHeight,
  },
  '& .MuiOutlinedInput-input': {
    paddingY: touchTargets.button.paddingY,
  },
};

/**
 * Touch-friendly styles for MUI Tab component
 * Ensures 44x44px minimum touch target
 */
export const touchFriendlyTab: SxProps<Theme> = {
  minWidth: touchTargets.minimum.width,
  minHeight: touchTargets.minimum.height,
  paddingX: touchTargets.button.paddingX,
};

/**
 * Touch-friendly styles for MUI Link component
 * Ensures adequate padding around clickable text
 */
export const touchFriendlyLink: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: touchTargets.minimum.height,
  paddingY: '10px',
  paddingX: '4px',
};

/**
 * Touch-friendly styles for MUI Select component
 * Ensures dropdown trigger has 44px height
 */
export const touchFriendlySelect: SxProps<Theme> = {
  '& .MuiSelect-select': {
    minHeight: touchTargets.button.minHeight,
    display: 'flex',
    alignItems: 'center',
  },
};

// ============================================================================
// GRID SPACING
// ============================================================================

/**
 * Mobile-friendly spacing between buttons in a row/group
 */
export const buttonGroupSpacing = mobileSpacing.betweenButtons;

/**
 * Mobile-friendly spacing between form inputs
 */
export const formInputSpacing = mobileSpacing.betweenInputs;

/**
 * Mobile-friendly spacing between major sections
 */
export const sectionSpacing = mobileSpacing.sectionGap;

// ============================================================================
// RESPONSIVE GRID
// ============================================================================

/**
 * Full-width on mobile (xs), auto on desktop
 * Use this for cards that should stack on mobile
 */
export const responsiveCardGrid = {
  xs: 12,    // Full width on mobile
  sm: 6,     // Half width on tablet
  md: 4,     // Third width on desktop
  lg: 3,     // Quarter width on large desktop
};

/**
 * Two-column layout on mobile, multi-column on desktop
 */
export const responsiveTwoColGrid = {
  xs: 12,    // Full width on mobile
  sm: 12,    // Full width on small tablet
  md: 6,     // Half width on desktop
  lg: 6,     // Half width on large desktop
};

/**
 * Three-column layout (only on desktop)
 */
export const responsiveThreeColGrid = {
  xs: 12,    // Full width on mobile
  sm: 12,    // Full width on tablet
  md: 4,     // Third width on desktop
  lg: 4,     // Third width on large desktop
};

// ============================================================================
// CHART RESPONSIVE HEIGHTS
// ============================================================================

/**
 * Responsive chart height based on screen size
 * Use with ResponsiveContainer or sx prop
 */
export const getResponsiveChartHeight = (
  size: 'small' | 'medium' | 'large' = 'medium'
): SxProps<Theme> => {
  const heights = {
    small: { xs: '200px', sm: '250px', md: '300px' },
    medium: { xs: '250px', sm: '300px', md: '400px' },
    large: { xs: '300px', sm: '400px', md: '500px' },
  };

  return {
    height: heights[size],
  };
};

// ============================================================================
// EXAMPLES
// ============================================================================

/**
 * @example Button with touch targets
 * ```tsx
 * <Button sx={touchFriendlyButton}>
 *   Calcular
 * </Button>
 * ```
 * 
 * @example IconButton with touch targets
 * ```tsx
 * <IconButton sx={touchFriendlyIconButton}>
 *   <IconInfo />
 * </IconButton>
 * ```
 * 
 * @example ToggleButton with touch targets
 * ```tsx
 * <ToggleButton value="real" sx={touchFriendlyToggleButton}>
 *   Real
 * </ToggleButton>
 * ```
 * 
 * @example Responsive Grid
 * ```tsx
 * <Grid container spacing={2}>
 *   <Grid item {...responsiveCardGrid}>
 *     <Card>...</Card>
 *   </Grid>
 * </Grid>
 * ```
 * 
 * @example Responsive Chart
 * ```tsx
 * <Box sx={getResponsiveChartHeight('medium')}>
 *   <ResponsiveContainer width="100%" height="100%">
 *     <LineChart>...</LineChart>
 *   </ResponsiveContainer>
 * </Box>
 * ```
 */
